<script setup lang="ts">
import type { GameWinnerResponse } from '@game/types'
import { useGameCombinedJackpot, useGameLatestJackpotWinner, useGameWinnersLatest, useI18n, useGameType } from '#imports'

// Combined jackpots and winners
const { combinedJackpot } = useGameCombinedJackpot()
const { data: winner } = useGameLatestJackpotWinner()
const { t } = useI18n()

export interface WinnersSidebarProps {
    horizontal?: boolean
    flippedOrder?: boolean
    showJackpotWinner?: boolean
    winnerListLimit?: number
    winnerTileSkeletons?: number
    title?: string
    shinyCombinedJackpot?: boolean
    fadeColor?: string | boolean
    titleColor?: string
    slugs?: string[]
}

const props = withDefaults(defineProps<WinnersSidebarProps>(), {
    horizontal: false,
    flippedOrder: false,
    showJackpotWinner: true,
    winnerListLimit: 9,
    winnerTileSkeletons: 9,
    title: undefined,
    shinyCombinedJackpot: false,
    fadeColor: true,
    titleColor: undefined,
})

const { isBingoSlug } = useGameType()
const { data: winnersLatest } = useGameWinnersLatest({ limit: props.winnerListLimit, slugs: props.slugs })

const jackpotWinnerLabel = (winner: GameWinnerResponse) => {
    if (isBingoSlug(winner.slug)) {
        return t('game.gameTile.mega_prize')
    }

    return t('game.gameTile.super_jackpot')
}
</script>

<template>
    <div
        :class="[
            'winners-sidebar',
            {
                'winners-sidebar--reversed': props.flippedOrder,
                'winners-sidebar--horizontal': props.horizontal,
            }
        ]"
        :style="typeof props.fadeColor === 'string' ? `--fade-color: ${props.fadeColor}` : undefined"
    >
        <div class="winners-sidebar__jackpot">
            <GameCombinedJackpot
                :jackpot="combinedJackpot || 0"
                :slim="props.horizontal"
                :shiny-amount="shinyCombinedJackpot"
            />

            <div v-if="showJackpotWinner" class="winners-sidebar__jackpot-winner">
                <GameWinnerTile v-if="winner && winner.gameName" :winner="winner" is-big-win />
                <GameWinnerTileSkeleton v-else />
                <span v-if="winner && winner.gameName" class="big-win__label">
                    {{ jackpotWinnerLabel(winner) }}
                </span>
            </div>
        </div>

        <h2
            v-if="props.title"
            class="winners-sidebar__title"
            :class="{ 'use-brand-font use-brand-font--bold': !props.horizontal }"
            :style="{ color: props.titleColor }"
        >
            {{ props.title }}
        </h2>

        <GameWinnerList
            v-if="winnersLatest?.length"
            :winners="winnersLatest"
            :horizontal="props.horizontal"
            :fade-color="props.fadeColor"
        />

        <div v-else class="winners-sidebar__latest-winners">
            <GameWinnerTileSkeleton v-for="index in winnerTileSkeletons" :key="index" />
            <div class="winners-sidebar__latest-winners__fade" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.winners-sidebar {
    display: flex;
    flex-direction: column;
    --fade-color: #{$winner-fade-color};

    &__title {
        text-align: center;
        font-size: $font-size-xl;
        margin: 17px 0 10px;
        grid-area: title;

        @media (min-width: $screen-lg) {
            margin: 33px 0 10px;
        }
    }

    &__latest-winners {
        padding: 0;
        margin: 0;
        position: relative;
        overflow: hidden;
        display: flex;
        position: relative;
        z-index: z(100);
        width: 100%;
        perspective: 500px;
        flex-direction: column;
        min-height: 52px;

        &__fade {
            position: absolute;
            bottom: -5px;
            left: 0;
            z-index: 2;
            width: 100%;
            height: 80px;
            background-image: linear-gradient(to bottom, transparent 0%, var(--fade-color) 100%);
            pointer-events: none;
        }
    }

    &__jackpot-winner {
        position: relative;

        .big-win__label {
            display: none;

            position: absolute;
            width: fit-content;
            top: 0;
            right: 10px;
            padding: 1px 5px 1px;
            border-radius: 0 0 6px 6px;
            color: $game-winner-big-win-label-color;
            background: $game-winner-big-win-label-bg;
            font-size: 11px;
            font-weight: 900;
            justify-self: flex-end;
            z-index: 1;
        }
    }

    &--horizontal {
        overflow: hidden;
        padding: 10px 0;
        margin-bottom: 10px;

        @include tablet {
            display: grid;
            grid-template-columns: auto max-content;
            grid-template-areas:
                'title title'
                'winners jackpot';
            grid-column-gap: 8px;
            margin-bottom: 0;
        }

        @include desktop {
            grid-template-columns: auto max-content;
            padding: 30px 0;

            &:first-child {
                padding-top: 0;
            }
        }

        &:has(.winners-sidebar__jackpot-winner) {
            @include desktop {
                grid-template-columns: auto 50%;
            }

            @include desktop-xl {
                grid-template-columns: auto 448px;
            }
        }

        .winners-sidebar__title {
            text-align: left;
            font-size: $font-size-lg;
            font-weight: $text-bold;
            margin: 0 0 10px;
        }

        .winners-sidebar__latest-winners {
            flex-direction: row;
            grid-area: winners;

            &__fade {
                width: 100%;
                background-image: linear-gradient(to right, transparent 92%, var(--fade-color) 100%);
            }
        }

        .winners-sidebar__jackpot {
            grid-area: jackpot;
            display: flex;
            flex-direction: row-reverse;
            gap: 8px;
        }

        .winners-sidebar__jackpot-winner {
            display: none;

            @include tablet(md) {
                display: block;
                width: 54%;
                flex: 1;

                .big-win__label {
                    display: block;
                }
            }

            @include desktop {
                width: 56%;
            }

            :deep(.game-winner-tile__skeleton) {
                background: $skeleton-color;
                opacity: 0.5;
            }
        }

        :deep(.combined-jackpot) {
            margin: 0;

            @include tablet(md) {
                display: block;
                width: 46%;

                .big-win__label {
                    display: block;
                }
            }

            @include desktop {
                width: 44%;
            }
        }

        :deep(.combined-jackpot:only-child) {
            @include tablet {
                width: max-content;
                min-width: 200px;
            }
        }

        :deep(.game-winner-tile__skeleton) {
            min-height: 50px;
        }

        :deep(.winner-tile) {
            margin-bottom: 0;
        }
    }

    &--reversed {
        flex-direction: column-reverse;

        .winners-sidebar__title {
            order: 1;
        }
    }
}
</style>
